import React from 'react';
import { Shield, Smartphone, PieChart, Users, Clock, Award, CheckCircle, TrendingUp, Lock, Zap } from 'lucide-react';
import { HeroSection } from '../components/HeroSection';
import { FeatureGrid } from '../components/FeatureGrid';
import { Testimonials } from '../components/Testimonials';
import { CTASection } from '../components/CTASection';
interface HomePageProps {
  onNavigate: (page: string) => void;
}
export function HomePage({
  onNavigate
}: HomePageProps) {
  const features = [{
    title: 'KI-gestützte Analyse',
    description: 'Unsere fortschrittliche KI analysiert Ihren Versicherungsbedarf in Sekunden und deckt Optimierungspotenzial auf.',
    icon: PieChart
  }, {
    title: 'SmartProtect App',
    description: 'Verwalten Sie alle Ihre Verträge zentral in einer preisgekrönten App. Digital, papierlos, sicher.',
    icon: Smartphone
  }, {
    title: 'Unabhängige Expertise',
    description: 'Wir sind zu 100% unabhängig und empfehlen nur, was Sie wirklich brauchen. Keine versteckten Provisionen.',
    icon: Shield
  }, {
    title: 'Premium Support 24/7',
    description: 'Echte Experten stehen Ihnen rund um die Uhr per Chat oder Videoanruf zur Verfügung.',
    icon: Users
  }, {
    title: 'Echtzeit-Abwicklung',
    description: 'Schäden einfach per App melden und Status in Echtzeit verfolgen. Auszahlung in Rekordzeit.',
    icon: Clock
  }, {
    title: 'Bestpreis-Garantie',
    description: 'Wir vergleichen über 300 Premium-Anbieter, um den optimalen Tarif für Ihre Bedürfnisse zu finden.',
    icon: Award
  }];
  const testimonials = [{
    name: 'Dr. Julia Weber',
    role: 'Zahnärztin',
    content: 'Endlich habe ich einen professionellen Überblick über meine Finanzen. SmartProtect24 ist intuitiv, sicher und der Support ist erstklassig.',
    rating: 5
  }, {
    name: 'Thomas Müller',
    role: 'Unternehmer',
    content: 'Der KI-Check hat mir geholfen, über 1.200€ im Jahr zu sparen und gleichzeitig meinen Schutz zu verbessern. Absolut empfehlenswert.',
    rating: 5
  }, {
    name: 'Sarah Klein',
    role: 'Architektin',
    content: 'Einfach, transparent und seriös. Genau so stelle ich mir modernes Finanzmanagement vor. Die App ist ein Gamechanger.',
    rating: 5
  }];
  return <div className="flex flex-col min-h-screen bg-white" data-id="element-415">
      <HeroSection title="Ihre Finanzen & Sicherheit. Intelligent. Premium." subtitle="Verwalten, vergleichen und optimieren Sie Ihren Schutz mit der führenden SmartProtect24-Plattform. Starten Sie heute in eine sichere Zukunft." primaryCtaText="App kostenlos nutzen" secondaryCtaText="Vergleich starten" onPrimaryClick={() => onNavigate('app')} onSecondaryClick={() => onNavigate('rechner')} benefits={['100% Kostenlos für Nutzer', 'TÜV-geprüfter Datenschutz', 'Über 300 Premium-Partner']} data-id="element-416" />

      {/* Premium Trust Section */}
      <section className="py-16 border-y border-slate-100 bg-white relative overflow-hidden" data-id="element-417">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_50%,rgba(59,130,246,0.03),transparent_50%)]" data-id="element-418"></div>
        <div className="container mx-auto px-4 text-center relative z-10" data-id="element-419">
          <div className="mb-12" data-id="element-420">
            <p className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-2" data-id="element-421">
              Ausgezeichnet von
            </p>
            <h3 className="text-2xl font-bold text-slate-900" data-id="element-422">
              Vertrauen Sie den Besten der Branche
            </h3>
          </div>
          <div className="flex flex-wrap justify-center items-center gap-16 md:gap-24" data-id="element-423">
            {[{
            name: 'Allianz',
            color: 'from-blue-600 to-blue-700'
          }, {
            name: 'AXA',
            color: 'from-red-600 to-pink-600'
          }, {
            name: 'Zurich',
            color: 'from-blue-700 to-indigo-700'
          }, {
            name: 'Generali',
            color: 'from-amber-600 to-orange-600'
          }, {
            name: 'Ergo',
            color: 'from-slate-700 to-slate-800'
          }].map((partner, i) => <div key={i} className="group cursor-pointer transition-all duration-300 hover:scale-110" data-id="element-424">
                <div className="relative" data-id="element-425">
                  <div className={`absolute inset-0 bg-gradient-to-r ${partner.color} rounded-xl blur-xl opacity-0 group-hover:opacity-20 transition-opacity duration-300`} data-id="element-426"></div>
                  <div className="relative flex items-center gap-3 px-6 py-4 bg-white rounded-xl border border-slate-100 shadow-sm group-hover:shadow-lg group-hover:border-slate-200 transition-all duration-300" data-id="element-427">
                    <Shield className="h-6 w-6 text-slate-400 group-hover:text-blue-600 transition-colors" data-id="element-428" />
                    <span className="text-xl font-bold text-slate-700 group-hover:text-slate-900 transition-colors" data-id="element-429">
                      {partner.name}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      <FeatureGrid title="Warum SmartProtect24?" subtitle="Wir verbinden modernste KI-Technologie mit persönlicher Premium-Expertise für Ihre maximale Sicherheit." features={features} data-id="element-430" />

      {/* Enhanced Stats Section */}
      <section className="py-24 lg:py-32 bg-slate-900 text-white relative overflow-hidden" data-id="element-431">
        {/* Animated Background */}
        <div className="absolute inset-0" data-id="element-432">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl animate-pulse" data-id="element-433"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl animate-pulse" style={{
          animationDelay: '1s'
        }} data-id="element-434"></div>
        </div>

        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,.02)_1px,transparent_1px)] bg-[size:64px_64px]" data-id="element-435"></div>

        <div className="container mx-auto px-4 relative z-10" data-id="element-436">
          <div className="text-center mb-16" data-id="element-437">
            <div className="inline-flex items-center gap-2 rounded-full bg-blue-500/10 px-4 py-1.5 text-sm font-semibold text-blue-400 mb-6 border border-blue-500/20" data-id="element-438">
              <TrendingUp className="h-4 w-4" data-id="element-439" />
              <span data-id="element-440">Zahlen, die überzeugen</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-441">
              SmartProtect24 in Zahlen
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto" data-id="element-442">
              Vertrauen Sie auf Erfahrung und Expertise
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12" data-id="element-443">
            {[{
            value: '50k+',
            label: 'Aktive Nutzer',
            icon: Users,
            color: 'from-blue-400 to-cyan-400'
          }, {
            value: '300+',
            label: 'Versicherungspartner',
            icon: Shield,
            color: 'from-indigo-400 to-purple-400'
          }, {
            value: '15M€',
            label: 'Verwaltetes Volumen',
            icon: TrendingUp,
            color: 'from-amber-400 to-orange-400'
          }, {
            value: '4.9',
            label: 'App Store Rating',
            icon: Award,
            color: 'from-green-400 to-emerald-400'
          }].map((stat, i) => <div key={i} className="group text-center" data-id="element-444">
                <div className="relative inline-block mb-6" data-id="element-445">
                  <div className={`absolute inset-0 bg-gradient-to-r ${stat.color} rounded-full blur-xl opacity-30 group-hover:opacity-50 transition-opacity duration-300`} data-id="element-446"></div>
                  <div className="relative bg-slate-800 p-4 rounded-full border border-slate-700 group-hover:border-slate-600 transition-colors" data-id="element-447">
                    <stat.icon className="h-8 w-8 text-slate-400 group-hover:text-white transition-colors" data-id="element-448" />
                  </div>
                </div>
                <div className={`text-5xl md:text-6xl font-bold mb-3 bg-gradient-to-r ${stat.color} bg-clip-text text-transparent`} data-id="element-449">
                  {stat.value}
                </div>
                <div className="text-slate-400 font-medium text-base md:text-lg" data-id="element-450">
                  {stat.label}
                </div>
              </div>)}
          </div>

          {/* Trust Indicators */}
          <div className="mt-20 pt-16 border-t border-slate-800" data-id="element-451">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto" data-id="element-452">
              {[{
              icon: Lock,
              text: 'Bank-Level Sicherheit',
              subtext: 'AES-256 Verschlüsselung'
            }, {
              icon: Shield,
              text: 'TÜV Zertifiziert',
              subtext: 'Geprüfte Qualität'
            }, {
              icon: Zap,
              text: 'Blitzschnell',
              subtext: 'Unter 3 Sekunden'
            }].map((item, i) => <div key={i} className="flex items-center gap-4 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50 hover:border-slate-600 transition-colors group" data-id="element-453">
                  <div className="bg-blue-500/10 p-3 rounded-lg group-hover:bg-blue-500/20 transition-colors" data-id="element-454">
                    <item.icon className="h-6 w-6 text-blue-400" data-id="element-455" />
                  </div>
                  <div data-id="element-456">
                    <div className="font-bold text-white" data-id="element-457">{item.text}</div>
                    <div className="text-sm text-slate-400" data-id="element-458">{item.subtext}</div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>

      <Testimonials items={testimonials} data-id="element-459" />

      <CTASection title="Bereit für das nächste Level?" description="Laden Sie die SmartProtect24 App herunter oder vereinbaren Sie ein exklusives Erstgespräch mit unseren Experten." primaryCtaText="Jetzt Premium-Schutz sichern" secondaryCtaText="Beratung anfragen" onPrimaryClick={() => onNavigate('app')} onSecondaryClick={() => onNavigate('beratung')} data-id="element-460" />
    </div>;
}